/**
 * Pure type-aware PropertyCard attribute builder (Issue #181).
 * Returns at most 3 non-empty { key, icon, label } facts. Never emits "—".
 */

const LAND_PURPOSE_UA = {
  residential: "Під житлову забудову",
  agricultural: "Сільгосппризначення",
  commercial: "Комерційне",
  industrial: "Промислове",
  recreational: "Рекреаційне",
  garden: "Садова / дачна",
};

const SUITABLE_FOR_UA = {
  office: "Офіс",
  retail: "Торгівля",
  warehouse: "Склад",
  restaurant: "Ресторан / кафе",
  hotel: "Готель",
  production: "Виробництво",
  mixed: "Змішане",
};

const CONDITION_UA = {
  none: "Без ремонту",
  raw: "Чорнова",
  needs_repair: "Потребує ремонту",
  cosmetic: "Косметичний",
  euro: "Євроремонт",
  designer: "Дизайнерський",
};

function formatArea(value) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return null;
  return `${numeric.toLocaleString("uk-UA", { maximumFractionDigits: 2 })} м²`;
}

function roomsAttr(property) {
  if (property.rooms == null) return null;
  return {
    key: "rooms",
    icon: "fa-solid fa-door-open",
    label: `${property.rooms} кімн.`,
  };
}

function areaAttr(property) {
  const text = formatArea(property.area);
  if (!text) return null;
  return {
    key: "area",
    icon: "fa-solid fa-vector-square",
    label: text,
  };
}

function floorAttr(property) {
  if (property.floor == null) return null;
  if (property.total_floors != null) {
    return {
      key: "floor",
      icon: "fa-solid fa-stairs",
      label: `${property.floor}/${property.total_floors} пов.`,
    };
  }
  return {
    key: "floor",
    icon: "fa-solid fa-stairs",
    label: `${property.floor} пов.`,
  };
}

function landAreaAttr(property) {
  const text = formatArea(property.land_area_m2);
  if (!text) return null;
  return {
    key: "land_area",
    icon: "fa-solid fa-mountain-sun",
    label: `ділянка ${text}`,
  };
}

function purposeAttr(property) {
  const label =
    LAND_PURPOSE_UA[property.land_purpose] ||
    property.land_purpose_label ||
    null;
  if (!label) return null;
  return {
    key: "purpose",
    icon: "fa-solid fa-signs-post",
    label,
  };
}

function roadUtilitiesAttr(property) {
  const parts = [];
  if (property.has_road_access) parts.push("під'їзд");
  if (property.has_utilities) parts.push("комунікації");
  if (!parts.length) return null;
  return {
    key: "access",
    icon: "fa-solid fa-road",
    label: parts.join(" · "),
  };
}

function suitableForAttr(property) {
  const label =
    SUITABLE_FOR_UA[property.suitable_for] ||
    property.suitable_for_label ||
    null;
  if (!label) return null;
  return {
    key: "suitable_for",
    icon: "fa-solid fa-briefcase",
    label,
  };
}

function conditionAttr(property) {
  const label =
    CONDITION_UA[property.condition] || property.condition_label || null;
  if (!label) return null;
  return {
    key: "condition",
    icon: "fa-solid fa-paint-roller",
    label,
  };
}

function pickFirst(...candidates) {
  return candidates.filter(Boolean).slice(0, 3);
}

export function getPropertyCardAttributes(property) {
  if (!property || typeof property !== "object") return [];

  switch (property.property_type) {
    case "apartment":
      return pickFirst(roomsAttr(property), areaAttr(property), floorAttr(property));
    case "house":
      return pickFirst(roomsAttr(property), areaAttr(property), landAreaAttr(property));
    case "land":
      return pickFirst(areaAttr(property), purposeAttr(property), roadUtilitiesAttr(property));
    case "commercial":
      return pickFirst(areaAttr(property), suitableForAttr(property), floorAttr(property));
    case "industrial":
      return pickFirst(
        areaAttr(property),
        suitableForAttr(property),
        floorAttr(property) || conditionAttr(property),
      );
    default:
      return pickFirst(roomsAttr(property), areaAttr(property), floorAttr(property));
  }
}

export default getPropertyCardAttributes;
